import { Injectable } from '@angular/core';
import { QrPosterBlock, QrPosterRenderContext } from '../models/qr-poster-template.model';

export interface QrPosterVariableGroup {
  labelKey: string;
  variables: Array<{
    token: string;
    labelKey: string;
  }>;
}

@Injectable({ providedIn: 'root' })
export class QrPosterVariableResolverService {
  private readonly commonGroups: QrPosterVariableGroup[] = [
    {
      labelKey: 'QR_POSTER_TEMPLATE.BUILDER.VARIABLE_GROUP_COMPANY',
      variables: [
        { token: '{{company.name}}', labelKey: 'QR_POSTER_TEMPLATE.BUILDER.VARIABLE_LABEL_COMPANY_NAME' },
        { token: '{{company.phone}}', labelKey: 'QR_POSTER_TEMPLATE.BUILDER.VARIABLE_LABEL_COMPANY_PHONE' },
        { token: '{{company.website}}', labelKey: 'QR_POSTER_TEMPLATE.BUILDER.VARIABLE_LABEL_COMPANY_WEBSITE' },
        { token: '{{company.address}}', labelKey: 'QR_POSTER_TEMPLATE.BUILDER.VARIABLE_LABEL_COMPANY_ADDRESS' },
        { token: '{{company.displayEmail}}', labelKey: 'QR_POSTER_TEMPLATE.BUILDER.VARIABLE_LABEL_COMPANY_DISPLAY_EMAIL' },
      ],
    },
    {
      labelKey: 'QR_POSTER_TEMPLATE.BUILDER.VARIABLE_GROUP_PROPERTY',
      variables: [
        { token: '{{property.address}}', labelKey: 'QR_POSTER_TEMPLATE.BUILDER.VARIABLE_LABEL_PROPERTY_ADDRESS' },
      ],
    },
  ];

  normalizeCompanyWebsite(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  hasCompanyWebsite(value: unknown): boolean {
    return this.normalizeCompanyWebsite(value).length > 0;
  }

  getVariableGroups(_appMode: string, context?: Pick<QrPosterRenderContext, 'companyWebsite'>): QrPosterVariableGroup[] {
    const hasCompanyWebsite = this.hasCompanyWebsite(context?.companyWebsite);

    return this.commonGroups.map((group) => ({
      ...group,
      variables: group.variables.filter((variable) => (
        variable.token !== '{{company.website}}' || hasCompanyWebsite
      )),
    }));
  }

  shouldRenderTemplateBlock(
    block: QrPosterBlock,
    context: Pick<QrPosterRenderContext, 'companyWebsite'>,
    templateBlocks: readonly QrPosterBlock[] = [],
  ): boolean {
    if (this.hasCompanyWebsite(context.companyWebsite)) {
      return true;
    }

    const isCompanyWebsiteRow = this.isCompanyWebsiteRow(block);
    const isModernWebsiteDivider = block.id === 'company-row-divider-2'
      && templateBlocks.some((candidate) => (
        candidate.id === 'company-row-website' && this.isCompanyWebsiteRow(candidate)
      ));

    return !isCompanyWebsiteRow && !isModernWebsiteDivider;
  }

  private isCompanyWebsiteRow(block: QrPosterBlock): boolean {
    const configuredText = block.content?.text || block.content?.variable || '';
    return block.type === 'iconText' && configuredText.trim() === '{{company.website}}';
  }

  resolveText(input: string | undefined, context: QrPosterRenderContext): string {
    if (!input) {
      return '';
    }

    return input.replace(/\{\{[^}]+\}\}/g, (token) => this.resolveVariable(token, context));
  }

  resolveVariable(variable: string | undefined, context: QrPosterRenderContext): string {
    switch ((variable || '').trim()) {
      case '{{company.name}}':
        return context.companyName || '';
      case '{{company.phone}}':
        return context.companyPhone || '';
      case '{{company.website}}':
        return this.normalizeCompanyWebsite(context.companyWebsite);
      case '{{company.address}}':
        return context.companyAddress || '';
      case '{{company.displayEmail}}':
        return context.companyDisplayEmail || '';
      case '{{company.logo}}':
        return context.companyLogoBase64 || '';
      case '{{company.themeColor}}':
        return context.themeColor || '';
      case '{{property.address}}':
        return context.propertyAddress || '';
      case '{{qr.url}}':
        return context.qrTargetUrl || '';
      case '{{qr.code}}':
        return '';
      case '{{poster.title}}':
        return context.posterTexts?.title || '';
      case '{{poster.subtitle}}':
        return context.posterTexts?.subtitle || '';
      case '{{poster.intro}}':
        return context.posterTexts?.intro || '';
      case '{{poster.addressHeader}}':
        return context.posterTexts?.addressHeader || '';
      case '{{poster.companyHeader}}':
        return context.posterTexts?.companyHeader || '';
      case '{{poster.cta}}':
        return context.posterTexts?.cta || '';
      case '{{poster.welcomeHeadline}}':
        return context.posterTexts?.welcomeHeadline || '';
      case '{{poster.welcomeSubheading}}':
        return context.posterTexts?.welcomeSubheading || '';
      case '{{poster.checkInInfo}}':
        return context.posterTexts?.checkInInfo || '';
      case '{{poster.wifiSnippet}}':
        return context.posterTexts?.wifiSnippet || '';
      case '{{poster.houseRulesTeaser}}':
        return context.posterTexts?.houseRulesTeaser || '';
      case '{{scanafy.website}}':
        return 'www.scanafy.de';
      default:
        return '';
    }
  }
}
